import { Component, OnInit } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { TooltipPosition } from '@angular/material/tooltip';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from "@angular/router";
import { eChartsAlign } from '../../../shared/enums/eCharts/echarts-align.enum';
import { eChartsPosition } from '../../../shared/enums/eCharts/echarts-position.enum';
import { eChartsVerticalAlign } from '../../../shared/enums/eCharts/echarts-vertical-align.enum';
import { DataCollection } from '../../../shared/models/site-content/data-collection.model';
import { Folder } from '../../../shared/models/site-content/folder.model';
import { FormInstance } from '../../../shared/models/site-content/form-instance.model';
import { Workflow } from '../../../shared/models/site-content/workflow.model';
import { MagicFolderResponseData } from '../../../shared/models/site-response-management/magic-folder-response-data.model';
import { MagicFormInstanceResponseData } from '../../../shared/models/site-response-management/magic-form-instance-response-data.model';
import { WorkflowCountData } from '../../../shared/models/site-response-management/workflow-count-data.model';
import { WorkflowResponseData } from '../../../shared/models/site-response-management/workflow-response-data.model';
import { WorkflowStateCountData } from '../../../shared/models/site-response-management/workflow-state-count-data.model';
import { WorkflowStateResponseData } from '../../../shared/models/site-response-management/workflow-state-response-data.model';
import { CurrentSiteService } from '../../../shared/services/current-site.service';
import { DataCollectionService } from '../../../shared/services/data-collection.service';
import { MagicStructureService } from '../../../shared/services/magic-structure.service';
import { SiteResponseManagementService } from '../../../shared/services/site-response-management.service';
import {
    ChartTypeInfo, ResponseManagementReportTypeInfo, ResponseManagementSiteModels
} from '../response-mgmt-config/response-mgmt-config.component';

// Constants.
// Report type constants.
const WorkflowMetricsReport: string = 'Workflow Metrics';
const WorkflowStateMetricsReport: string = 'Workflow State Metrics';
const MagicFolderMetricsReport: string = 'Magic Folder Metrics';
const MagicFormMetricsReport: string = 'Magic Form Metrics';

@Component({
    selector: 'app-response-management',
    templateUrl: './response-management.component.html',
    styleUrls: ['./response-management.component.scss'],
    standalone: false
})
export class ResponseManagementComponent implements OnInit {
    // Read-only data.
    private readonly reportTypes: ResponseManagementReportTypeInfo[] = null; // Have to initialize this later due to some referenced arrow functions.
    private readonly chartTypes: ChartTypeInfo[] =
        [
            { chartType: 'Bar', chartId: 1, packageDataFunction: this.getExampleBarChartOptions },
            { chartType: 'Pie', chartId: 2, packageDataFunction: this.getExamplePieChartOptions },
            { chartType: 'Graph', chartId: 3, packageDataFunction: this.getExampleCartesian2dChartOptions },
            { chartType: 'Tree', chartId: 4, packageDataFunction: this.getExampleTreeChartOptions }
        ];

    // Properties.
    private siteWorkflows: Workflow[] = [];
    private magicFolders: Folder[] = [];
    private magicFormInstances: FormInstance[] = [];

    // Private properties.
    private iSelectedReportTypeId: number = 0;
    private iSelectedChartTypeId: number = 0;

    private iSelectedWorkflowId: number = 0;
    private iSelectedMagicFolderId: number = 0;
    private iSelectedMagicFormInstanceId: number = 0;

    private fnOptionsFunction: () => any = null;
    private readonly fnOptionsFunctions: (() => any)[] =
        [
            this.getExampleBarChartOptions,
            this.getExamplePieChartOptions,
            this.getExampleCartesian2dChartOptions,
            this.getExampleTreeChartOptions
        ];
    private eChartsOptions: any = null;

    // Constructor.
    public constructor(private currentSiteService: CurrentSiteService,
        private dataCollectionService: DataCollectionService,
        private magicStructureService: MagicStructureService,
        private route: ActivatedRoute,
        private readonly titleService: Title,
        private siteResponseManagementService: SiteResponseManagementService) {
        this.reportTypes =
            [
                new ResponseManagementReportTypeInfo(
                    {
                        reportType: WorkflowMetricsReport,
                        reportId: 1,
                        serviceMethodName: 'getWorkflowMetrics',
                        packageDataFunction: this.packageWorkflowMetricsData
                    }),
                new ResponseManagementReportTypeInfo(
                    {
                        reportType: WorkflowStateMetricsReport,
                        reportId: 2,
                        workflowRequired: true,
                        serviceMethodName: 'getWorkflowStateMetrics',
                        packageDataFunction: this.packageWorkflowStateMetricsData
                    }),
                new ResponseManagementReportTypeInfo(
                    {
                        reportType: MagicFolderMetricsReport,
                        reportId: 3,
                        magicFolderRequired: true,
                        serviceMethodName: 'getMagicFolderMetrics',
                        packageDataFunction: this.packageMagicFolderMetricsData
                    }),
                new ResponseManagementReportTypeInfo(
                    {
                        reportType: MagicFormMetricsReport,
                        reportId: 4,
                        magicFormInstanceRequired: true,
                        serviceMethodName: 'getMagicFormInstanceMetrics',
                        packageDataFunction: this.packageMagicFormInstanceMetricsData
                    })
            ];

        return;
    }

    // Life cycle methods.
    public ngOnInit(): void {
        // Access the current site.
        let currentSite: DataCollection = this.currentSiteService.Site;

        // Get site workflows, magic folders, and magic form instances.
        this.dataCollectionService.getWorkflowsBySite(currentSite.id).then(responseWorkflows => {
            this.siteWorkflows = responseWorkflows;
        });
        this.magicStructureService.getMagicFolders(currentSite.id).then(responseMagicFolders => {
            this.magicFolders = responseMagicFolders;
        });
        this.magicStructureService.getMagicFormInstances(currentSite.id).then(responseMagicFormInstances => {
            this.magicFormInstances = responseMagicFormInstances;
        });

        this.titleService.setTitle(this.route.snapshot.data['title']);
        return;
    }

    // 'Accessor' methods called by my HTML code.
    public get Title(): string {
        return ('Site Response Management');
    }

    public get ReportTypes(): ResponseManagementReportTypeInfo[] {
        return (this.reportTypes);
    }
    public get ChartTypes(): ChartTypeInfo[] {
        return (this.chartTypes);
    }

    public get SiteWorkflows(): Workflow[] {
        return (this.siteWorkflows);
    }
    public get MagicFolders(): Folder[] {
        return (this.magicFolders);
    }
    public get MagicFormInstances(): FormInstance[] {
        return (this.magicFormInstances);
    }

    public get SelectionMissing(): boolean {
        let bMissing: boolean = true;

        if (this.iSelectedReportTypeId > 0) {
            let reportType: ResponseManagementReportTypeInfo = this.reportTypes[this.iSelectedReportTypeId - 1];

            if (reportType.workflowRequired) {
                //if ((this.iSelectedWorkflowId > 0) || (this.iSelectedChartTypeId > 0)) {
                if (this.iSelectedWorkflowId > 0) {
                    bMissing = false;
                }
            } else if (reportType.magicFolderRequired) {
                if (this.iSelectedMagicFolderId > 0) {
                    bMissing = false;
                }
            } else if (reportType.magicFormInstanceRequired) {
                if (this.iSelectedMagicFormInstanceId > 0) {
                    bMissing = false;
                }
            } else {
                bMissing = false;
            }
        }

        return (bMissing);
    }

    public get TooltipPosition(): TooltipPosition {
        let position: TooltipPosition = 'below';

        return (position);
    }
    public get DrawChartButtonTooltip(): string {
        return ('Click to draw a chart');
    }

    public get EChartsOptions(): any {
        return (this.eChartsOptions);
    }

    // Handle control events.
    public getSiteWorkflows(eventData: ResponseManagementSiteModels): void {
        eventData.siteWorkflows = this.siteWorkflows;
    }
    public getMagicFolders(eventData: ResponseManagementSiteModels): void {
        eventData.magicFolders = this.magicFolders;
    }
    public getMagicFormInstances(eventData: ResponseManagementSiteModels): void {
        eventData.magicFormInstances = this.magicFormInstances;
    }

    public reportTypeSelectionChanged(eventData: MatSelectChange): void {
        this.iSelectedReportTypeId = eventData.value;

        this.drawChartIfConfigComplete();
    }

    public workflowSelectionChanged(eventData: MatSelectChange): void {
        this.iSelectedWorkflowId = eventData.value;

        this.drawChartIfConfigComplete();
    }
    public magicFolderSelectionChanged(eventData: MatSelectChange): void {
        this.iSelectedMagicFolderId = eventData.value;

        this.drawChartIfConfigComplete();
    }
    public magicFormInstanceSelectionChanged(eventData: MatSelectChange): void {
        this.iSelectedMagicFormInstanceId = eventData.value;

        this.drawChartIfConfigComplete();
    }

    public chartTypeSelectionChanged(eventData: MatSelectChange): void {
        this.iSelectedChartTypeId = eventData.value;

        if (this.iSelectedChartTypeId > 0) {
            let iChartIndex: number = this.iSelectedChartTypeId - 1;
            this.fnOptionsFunction = <() => any>this.fnOptionsFunctions[iChartIndex];

            this.eChartsOptions = this.fnOptionsFunction();
        }
    }

    public drawChart(): void {
        let reportType: ResponseManagementReportTypeInfo = this.reportTypes[this.iSelectedReportTypeId - 1];

        if (reportType.reportType == WorkflowMetricsReport) {
            let currentSite: DataCollection = this.currentSiteService.Site;

            this.siteResponseManagementService.getWorkflowMetrics(currentSite.id).then(response => {
                this.packageReportData(response);
            });
        } else if (reportType.reportType == WorkflowStateMetricsReport) {
            let workflow: Workflow = this.siteWorkflows.find(wf => wf.id == this.iSelectedWorkflowId);

            if (workflow != null) {
                this.siteResponseManagementService.getWorkflowStateMetrics(workflow.id).then(response => {
                    this.packageReportData(response);
                });
            }
        } else if (reportType.reportType == MagicFolderMetricsReport) {
            let magicFolder: Folder = this.magicFolders.find(mf => mf.id == this.iSelectedMagicFolderId);

            if (magicFolder != null) {
                this.siteResponseManagementService.getMagicFolderMetrics(magicFolder.id).then(response => {
                    this.packageReportData(response);
                });
            }
        } else if (reportType.reportType == MagicFormMetricsReport) {
            let magicFormInstance: FormInstance = this.magicFormInstances.find(mfi => mfi.id == this.iSelectedMagicFormInstanceId);

            if (magicFormInstance != null) {
                this.siteResponseManagementService.getMagicFormInstanceMetrics(magicFormInstance.id).then(response => {
                    this.packageReportData(response);
                });
            }
        }

        return;
    }

    // Define methods for packaging report data.
    private drawChartIfConfigComplete(): void {
        if (!this.SelectionMissing) {
            this.drawChart();
        }
    }

    private packageReportData(metricsParam: any): void {
        //if ((this.iSelectedReportTypeId > 0) && (this.iSelectedChartTypeId > 0)) {
        if (this.iSelectedReportTypeId > 0) {
            let reportType: ResponseManagementReportTypeInfo = this.reportTypes[this.iSelectedReportTypeId - 1];

            this.eChartsOptions = reportType.packageDataFunction(metricsParam);
        }

        return;
    }

    private packageWorkflowMetricsData = (metricsParam: any): any => {
        // Assemble/compile the report data.
        let metrics: WorkflowResponseData = <WorkflowResponseData>metricsParam;

        let xAxisLabels: string[] = [];
        let formInstanceData: number[] = [];
        let folderData: number[] = [];

        for (let iWorkflowIndex: number = 0; iWorkflowIndex < metrics.workflowCountData.length; iWorkflowIndex++) {
            let workflowMetrics: WorkflowCountData = metrics.workflowCountData[iWorkflowIndex];

            xAxisLabels.push(workflowMetrics.workflowName);
            formInstanceData.push(workflowMetrics.formInstanceTotalCount);
            folderData.push(workflowMetrics.folderTotalCount);
        }

        /*
        // Package the chart options data.
        let labelOptions: any = this.getLabelOptions();

        let options = {
            xAxis: {
                type: 'category',
                data: xAxisLabels
            },
            yAxis: {
                type: 'value'
            },
            series:
                [
                    {
                        name: 'Forms',
                        data: formInstanceData,
                        type: 'bar',
                        label: labelOptions,
                        itemStyle: { color: 'blue' }
                    },
                    {
                        name: 'Folders',
                        data: folderData,
                        type: 'bar',
                        label: labelOptions,
                        itemStyle: { color: 'green' }
                    }
                ]
        };
        */
        let options = ResponseManagementComponent.packageFormInstancesFoldersChart(xAxisLabels, formInstanceData, folderData);
        return (options);
    }

    private packageWorkflowStateMetricsData = (metricsParam: any): any => {
        let metrics: WorkflowStateResponseData = <WorkflowStateResponseData>metricsParam;

        let xAxisLabels: string[] = [];
        let formInstanceData: number[] = [];
        let folderData: number[] = [];

        for (let iStateIndex: number = 0; iStateIndex < metrics.workflowStateCounts.length; iStateIndex++) {
            let stateMetrics: WorkflowStateCountData = metrics.workflowStateCounts[iStateIndex];

            xAxisLabels.push(stateMetrics.workflowStateName);
            formInstanceData.push(stateMetrics.formInstanceCount);
            folderData.push(stateMetrics.folderCount);
        }

        let options = ResponseManagementComponent.packageFormInstancesFoldersChart(xAxisLabels, formInstanceData, folderData);
        return (options);
    }

    private packageMagicFolderMetricsData = (metricsParam: any): any => {
        let metrics: MagicFolderResponseData = <MagicFolderResponseData>metricsParam;

        let options: any = ResponseManagementComponent.packageMagicStructureChart(metrics.fulfilledMembershipCount, metrics.unfulfilledMembershipCount);
        return (options);
    }

    private packageMagicFormInstanceMetricsData = (metricsParam: any): any => {
        let metrics: MagicFormInstanceResponseData = <MagicFormInstanceResponseData>metricsParam;

        let options: any = ResponseManagementComponent.packageMagicStructureChart(metrics.fulfilledMembershipCount, metrics.unfulfilledMembershipCount);
        return (options);
    }

    private static packageFormInstancesFoldersChart(xAxisLabels: string[], formInstanceData: number[], folderData: number[]): any {
        // Package the chart options data.
        let labelOptions: any = ResponseManagementComponent.getLabelOptions();

        let options = {
            xAxis: {
                type: 'category',
                data: xAxisLabels
            },
            yAxis: {
                type: 'value'
            },
            series:
                [
                    {
                        name: 'Forms',
                        data: formInstanceData,
                        type: 'bar',
                        label: labelOptions,
                        itemStyle: { color: 'blue' }
                    },
                    {
                        name: 'Folders',
                        data: folderData,
                        type: 'bar',
                        label: labelOptions,
                        itemStyle: { color: 'green' }
                    }
                ]
        };

        return (options);
    }

    private static packageMagicStructureChart(numFulfilledMemberships: number, numUnfulfilledMemberships: number): any {
        //let labelOptions: any = ResponseManagementComponent.getLabelOptions();

        let options = {
            xAxis: {
                type: 'category',
                data: ['Fulfilled Memberships', 'Unfulfilled Memberships']
            },
            yAxis: {
                type: 'value'
            },
            series:
                [
                    {
                        data: [numFulfilledMemberships, numUnfulfilledMemberships],
                        type: 'bar'
                    }
                ]
        };

        return (options);
    }

    private static getLabelOptions(): any {
        let labelOptions = {
            show: true,
            /*
            position: app.config.position,
            distance: app.config.distance,
            align: app.config.align,
            verticalAlign: app.config.verticalAlign,
            rotate: app.config.rotate,
            */
            formatter: '{c}  {name|{a}}',
            fontSize: 16,
            rotate: 90,

            position: eChartsPosition.insideBottom, //'insideBottom',
            distance: 15, // Perhaps this is in pixels?
            align: eChartsAlign.left,
            verticalAlign: eChartsVerticalAlign.middle,

            rich: {
                name: {
                }
            }
        };

        return (labelOptions);
    }

    // Define methods used only during tests of eChart/Ngx-eCharts.
    private getExampleBarChartOptions(): any {
        /*
        let options = {
            xAxis: {
                type: 'category',
                data: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
            },
            yAxis: {
                type: 'value'
            },
            series: [{
                data: [120, 200, 150, 80, 70, 110, 130],
                type: 'bar'
            }]
        };
        */
        let labelOption = {
            show: true,
            /*
            position: app.config.position,
            distance: app.config.distance,
            align: app.config.align,
            verticalAlign: app.config.verticalAlign,
            rotate: app.config.rotate,
            */
            formatter: '{c}  {name|{a}}',
            fontSize: 16,
            rotate: 90,
            rich: {
                name: {
                }
            }
        };

        let options = {
            xAxis: {
                type: 'category',
                data: ['WF 1', 'WF 2', 'WF 3', 'WF 4', 'WF 5', 'WF 6', 'WF 7']
            },
            yAxis: {
                type: 'value'
            },
            /*
            legend: {
                data: ['Forms', 'Folders']
            },
            */
            series:
                [
                    {
                        name: 'Forms',
                        data: [120, 20, 150, 10, 70, 20, 130],
                        type: 'bar',
                        label: labelOption
                    },
                    {
                        name: 'Folders',
                        data: [10, 200, 20, 80, 10, 110, 20],
                        type: 'bar',
                        label: labelOption
                    }
                ]
        };

        return (options);
    }

    private getExamplePieChartOptions(): any {
        let options = {
            title: {
                text: 'How Users Are Finding the Website',
                subtext: 'Fictitious',
                x: 'center'
            },
            tooltip: {
                trigger: 'item',
                formatter: "{a} <br/>{b} : {c} ({d}%)"
            },
            legend: {
                orient: 'vertical',
                left: 'left',
                data: ['Direct Interview', 'E-Mail Marketing', 'Advertising Alliance', 'Video Ads', 'Search Engine']
            },
            series: [
                {
                    name: 'Access Sources',
                    type: 'pie',
                    radius: '55%',
                    center: ['50%', '60%'],
                    data: [
                        { value: 335, name: 'Direct Interview' },
                        { value: 310, name: 'E-Mail Marketing' },
                        { value: 234, name: 'AA' },
                        { value: 135, name: 'Video Ads' },
                        { value: 1548, name: 'Search Engine' }
                    ],
                    itemStyle: {
                        emphasis: {
                            shadowBlur: 0,
                            shadowOffsetX: 0,
                            shadowColor: 'rgba(0, 0, 0, 0)'

                        }
                    }
                }
            ]
        };

        return (options);
    }

    private getExampleCartesian2dChartOptions(): any {
        let options = {
            tooltip: {
                position: 'left',
            },
            grid: {
                left: '7%',
                right: '3%',
            },
            animation: false,
            xAxis: {
                type: 'category',
                data: ['04-10-2020', '05-10-2020'],
                splitArea: {
                    show: false
                },
                position: 'top'
            },
            yAxis: {
                type: 'category',
                data: ['A', 'B'],
                splitLine: {
                    show: true,
                    lineStyle: {
                        width: 2
                    }
                }
            },
            visualMap: [{
                type: 'continuous',
                show: false,
                min: 0,
                max: 7,
                inRange: {
                    color: ['#ffffff', '#990000']
                },
                dimension: '2',
            }, {
                type: 'continuous',
                show: false,
                min: 7,
                max: 15,
                inRange: {
                    color: ['#AC33FF', '#FF7C00']
                },
                dimension: '2',
            }

            ],
            series: {
                name: "test",
                type: 'heatmap',
                data: [
                    [0, 0, 2],
                    [0, 1, 5],
                    [1, 0, 9],
                    [1, 1, 12]
                ],
                label: {
                    show: false
                },
                coordinateSystem: 'cartesian2d',
                emphasis: {
                    itemStyle: {
                        shadowBlur: 10,
                        shadowColor: 'rgba(0, 0, 0, 0.5)'
                    }
                }
            }
        };

        return (options);
    }

    private getExampleTreeChartOptions(): any {
        let options = {
            title: {
                text: 'Ice Bucket Challenge'
            },
            toolbox: {
                show: true,
                feature: {
                    mark: { show: true },
                    dataView: { show: true, readOnly: false },
                    restore: { show: true },
                    saveAsImage: { show: true }
                }
            },
            series: [
                {
                    name: 'Fig Tree',
                    type: 'tree',
                    orient: 'horizontal',  // vertical horizontal
                    rootLocation: { x: 100, y: 'center' }, // 根节点位置  {x: 100, y: 'center'}
                    nodePadding: 8,
                    layerPadding: 200,
                    hoverable: false,
                    roam: true,
                    symbolSize: 6,
                    itemStyle: {
                        normal: {
                            color: '#4883b4',
                            label: {
                                show: true,
                                position: 'right',
                                formatter: "{b}",
                                textStyle: {
                                    color: '#000',
                                    fontSize: 12
                                }
                            },
                            lineStyle: {
                                color: '#ccc',
                                type: 'curve' // 'curve'|'broken'|'solid'|'dotted'|'dashed'

                            }
                        },
                        emphasis: {
                            color: '#4883b4',
                            label: {
                                show: false
                            },
                            borderWidth: 0
                        }
                    },

                    data: [{


                        "name": "Ice Bucket Challenge",

                        "children": [{
                            "name": "Liu Zuohu",
                            "children": [{
                                "name": "Zhou",
                                "children": [{
                                    "name": "Ma"
                                }, {
                                    "name": "Xu Xiaoping",
                                    "children": [{
                                        "name": "Niu Wenwen",
                                        "children": [{
                                            "name": "Yao Jinbo",
                                            "children": [{
                                                "name": "Cai Wensheng"
                                            }, {
                                                "name": "Cai"
                                            }, {
                                                "name": "Wang Fei"
                                            }]
                                        }, {
                                            "name": "Yangshou Bin",
                                            "children": [{
                                                "name": "All entrepreneurs"
                                            }, {
                                                "name": "all investors"
                                            }, {
                                                "name": "All of entrepreneurial services"
                                            }]
                                        }, {
                                            "name": "Pu Yi"
                                        }]
                                    }, {
                                        "name": "Luo Zhenyu",
                                        "children": [{
                                            "name": "Luo Ji thinking 25000 Member"
                                        }]
                                    }, {
                                        "name": "Wong"
                                    }]
                                }, {
                                    "name": "Huang Zhang"
                                }]
                            }, {
                                "name": "Overheating"
                            }, {
                                "name": "Liu Jiangfeng",
                                "children": [{
                                    "name": "He just",
                                    "children": [{
                                        "name": "Xie Qingjiang"
                                    }, {
                                        "name": "Wang Xiang"
                                    }, {
                                        "name": "Wei Ai"
                                    }]
                                }, {
                                    "name": "Wang Yulei"
                                }]
                            }]
                        }, {
                            "name": "Lei Jun",
                            "children": [{
                                "name": "Andy",
                                "children": [{
                                    "name": "Lang Lang"
                                }, {
                                    "name": "Su Hua Wei"
                                }, {
                                    "name": "Jay",
                                    "children": [{
                                        "name": "Vincent Fang",
                                        "children": [{
                                            "name": "Giddens"
                                        }, {
                                            "name": "Alan Kuo"
                                        }]
                                    }, {
                                        "name": "Mayday",
                                        "children": [{
                                            "name": "Xie Jin Yan",
                                            "children": [{
                                                "name": "Zhao Hui Sin"
                                            }, {
                                                "name": "Zhang Fei",
                                                "children": [{
                                                    "name": "Little S"
                                                }]
                                            }, {
                                                "name": "Aaron"
                                            }]
                                        }, {
                                            "name": "Olivia"
                                        }, {
                                            "name": "Takeshi Kaneshiro"
                                        }]
                                    }]
                                }]
                            }, {
                                "name": "Li",
                                "children": [{
                                    "name": "Mr Yu"
                                }, {
                                    "name": "Pan Shiyi",
                                    "children": [{
                                        "name": "Ren"
                                    }]
                                }, {
                                    "name": "Tian Liang",
                                    "children": [{
                                        "name": "Wang Yuelun"
                                    }, {
                                        "name": "Little Shenyang"
                                    }, {
                                        "name": "Li Xiaopeng"
                                    }]
                                }]
                            }, {
                                "name": "Gou",
                                "children": [{
                                    "name": "Son",
                                    "children": [{
                                        "name": "Miyasaka learning"
                                    }]
                                }, {
                                    "name": "Xiaoliang Sunney Xie"
                                }, {
                                    "name": "Lin Chi-ling"
                                }]
                            }]
                        }, {
                            "name": "Koo",
                            "children": [{
                                "name": "Ma"
                            }, {
                                "name": "Wang Changtian",
                                "children": [{
                                    "name": "Deng Chao",
                                    "children": [{
                                        "name": "Yu browed",
                                        "children": [{
                                            "name": "Yao Chen",
                                            "children": [{
                                                "name": "Wu Xiubo"
                                            }, {
                                                "name": "Daniel Wu"
                                            }, {
                                                "name": "Honglei"
                                            }]
                                        }, {
                                            "name": "Zhu Fangyu",
                                            "children": [{
                                                "name": "Wang Shipeng",
                                                "children": [{
                                                    "name": "Yi Jianlian"
                                                }]
                                            }]
                                        }, {
                                            "name": "Liang Chao"
                                        }]
                                    }]
                                }, {
                                    "name": "Liu Yifei"
                                }, {
                                    "name": "Liu with"
                                }]
                            }]
                        }, {
                            "name": "Zhu Ting",
                            "children": [{
                                "name": "Zhang Yaokun",
                                "children": [{
                                    "name": "Jiang Ning"
                                }, {
                                    "name": "Tang Miao"
                                }]
                            }, {
                                "name": "Zhou Haibin",
                                "children": [{
                                    "name": "Wang Song",
                                    "children": [{
                                        "name": "Cai Yun"
                                    }, {
                                        "name": "Lee Yi Feng"
                                    }, {
                                        "name": "Wang Tao"
                                    }]
                                }, {
                                    "name": "Shao"
                                }, {
                                    "name": "Gaudi",
                                    "children": [{
                                        "name": "Moreno"
                                    }, {
                                        "name": "Enrique"
                                    }, {
                                        "name": "Paul"
                                    }]
                                }]
                            }, {
                                "name": "Little Yan Chuang"
                            }]
                        }, {
                            "name": "Zheng Lu",
                            "children": [{
                                "name": "Ka",
                                "children": [{
                                    "name": "Dong Chengpeng",
                                    "children": [{
                                        "name": "Iraq a",
                                        "children": [{
                                            "name": "Xu Song"
                                        }, {
                                            "name": "pay simbo"
                                        }, {
                                            "name": "Hong Chen"
                                        }]
                                    }, {
                                        "name": "Wong Cho Lam",
                                        "children": [{
                                            "name": "Ivana"
                                        }, {
                                            "name": "Leanne",
                                            "children": [{
                                                "name": "Song Hee Year"
                                            }, {
                                                "name": "Linda Chung"
                                            }, {
                                                "name": "Macy Chan"
                                            }]
                                        }, {
                                            "name": "Jia Ling"
                                        }]
                                    }, {
                                        "name": "off white",
                                        "children": [{
                                            "name": "Sheng Yu",
                                            "children": [{
                                                "name": "evil child"
                                            }, {
                                                "name": "Du Haitao"
                                            }, {
                                                "name": "Wang Han"
                                            }]
                                        }, {
                                            "name": "faction Specter"
                                        }, {
                                            "name": "thank God",
                                            "children": [{
                                                "name": "C-BLOCK chubby"
                                            }, {
                                                "name": "Fan Yuancheng"
                                            }, {
                                                "name": "Mike Sui",
                                                "children": [{
                                                    "name": "Godfrey"
                                                }, {
                                                    "name": "Jingle Ma"
                                                }]
                                            }]
                                        }]
                                    }]
                                }, {
                                    "name": "Yi Jianlian, Li Ai, JiangYingRong"
                                }]
                            }, {
                                "name": "Kevin Han"
                            }]
                        }, {
                            "name": "Shu Dewei",
                            "children": [{
                                "name": "Yao Ming"
                            }, {
                                "name": "NBA China staff"
                            }]
                        }, {
                            "name": "Ye propylene into",
                            "children": [{
                                "name": "The Joe Zhai"
                            }, {
                                "name": "Ji Xiaohua",
                                "children": [{
                                    "name": "Sicong",
                                    "children": [{
                                        "name": "Easy to revitalize",
                                        "children": [{
                                            "name": "Xu Lei"
                                        }, {
                                            "name": "Tong Dawei",
                                            "children": [{
                                                "name": "Meng non",
                                                "children": [{
                                                    "name": "Guo Degang",
                                                    "children": [{
                                                        "name": "Yu Qian"
                                                    }]
                                                }, {
                                                    "name": "Huang",
                                                    "children": [{
                                                        "name": "ZhangLinPeng",
                                                        "children": [{
                                                            "name": "Huang Bowen"
                                                        }, {
                                                            "name": "Shuai"
                                                        }]
                                                    }, {
                                                        "name": "Gao Lin",
                                                        "children": [{
                                                            "name": "Jianhong"
                                                        }, {
                                                            "name": "Li Weifeng"
                                                        }]
                                                    }]
                                                }]
                                            }, {
                                                "name": "Kun"
                                            }, {
                                                "name": "AKB48"
                                            }]
                                        }, {
                                            "name": "Wu Xinhong",
                                            "children": [{
                                                "name": "Jia Nailiang"
                                            }, {
                                                "name": "Lu Lu"
                                            }, {
                                                "name": "angelababy"
                                            }]
                                        }]
                                    }, {
                                        "name": "Lin update",
                                        "children": [{
                                            "name": "Chao"
                                        }, {
                                            "name": "Tong Liya"
                                        }, {
                                            "name": "AngelaBaby"
                                        }]
                                    }, {
                                        "name": "Liu Jun"
                                    }]
                                }, {
                                    "name": "Wei Kunlin",
                                    "children": [{
                                        "name": "Chi Perceived"
                                    }, {
                                        "name": "Li Miao"
                                    }, {
                                        "name": "Jiang Zhenyu"
                                    }]
                                }, {
                                    "name": "Liu Chengcheng",
                                    "children": [{
                                        "name": "Zhang Ying"
                                    }, {
                                        "name": "King freely",
                                        "children": [{
                                            "name": "Liu Xiang"
                                        }, {
                                            "name": "Wu"
                                        }, {
                                            "name": "Fu Sheng"
                                        }]
                                    }, {
                                        "name": "Wang Feng"
                                    }]
                                }]
                            }]
                        }, {
                            "name": "Xiao on agriculture",
                            "children": [{
                                "name": "Chen Lin",
                                "children": [{
                                    "name": "Ko Wen-je",
                                    "children": [{
                                        "name": "Jaw"
                                    }, {
                                        "name": "Wei Te-sheng"
                                    }, {
                                        "name": "Guo dry"
                                    }]
                                }, {
                                    "name": "straight text",
                                    "children": [{
                                        "name": "Hau",
                                        "children": [{
                                            "name": "Luc Besson"
                                        }, {
                                            "name": "Hu"
                                        }, {
                                            "name": "Wen-Ta Chiu"
                                        }]
                                    }, {
                                        "name": "Chu"
                                    }, {
                                        "name": "Wu Se-hwa"
                                    }]
                                }, {
                                    "name": "Kuan Chung-ming",
                                    "children": [{
                                        "name": "Woody Duh"
                                    }, {
                                        "name": "Chen Bao-ji"
                                    }, {
                                        "name": "Yang Panchi"
                                    }]
                                }]
                            }, {
                                "name": "Chen Sulan",
                                "children": [{
                                    "name": "Yen",
                                    "children": [{
                                        "name": "Zhan Hongzhi",
                                        "children": [{
                                            "name": "Niu Ze"
                                        }, {
                                            "name": "Jonathan Lee"
                                        }, {
                                            "name": "He Feipeng"
                                        }]
                                    }, {
                                        "name": "Chen Qingxiang",
                                        "children": [{
                                            "name": "Can a Yellow Sun"
                                        }, {
                                            "name": "Huang Shenghua"
                                        }, {
                                            "name": "Wu Qi liter"
                                        }]
                                    }]
                                }]
                            }, {
                                "name": "Cai herders"
                            }]
                        }, {
                            "name": "Jeremy Lin",
                            "children": [{
                                "name": "Landry - Fields"
                            }, {
                                "name": "Parsons"
                            }]
                        }, {
                            "name": "Wang Meng",
                            "children": [{
                                "name": "Yang Yi"
                            }, {
                                "name": "Ke Fan"
                            }, {
                                "name": "StephonMarbury"
                            }]
                        }, {
                            "name": "Michelle",
                            "children": [{
                                "name": "Li Chen"
                            }, {
                                "name": "somans",
                                "children": [{
                                    "name": "Huang Xiaoming",
                                    "children": [{
                                        "name": "Fan Bingbing"
                                    }, {
                                        "name": "Joke",
                                        "children": [{
                                            "name": "Wang Jun"
                                        }, {
                                            "name": "Sina"
                                        }]
                                    }]
                                }]
                            }, {
                                "name": "Chen Ou"
                            }]
                        }, {
                            "name": "Zhang Ziyi",
                            "children": [{
                                "name": "TFBoys",
                                "children": [{
                                    "name": "Shang Geyun Dayton"
                                }]
                            }, {
                                "name": "Han Geng",
                                "children": [{
                                    "name": "Michael Bay"
                                }, {
                                    "name": "He Jiong"
                                }, {
                                    "name": "Na Ying"
                                }]
                            }, {
                                "name": "Sophie Marceau"
                            }]
                        }, {
                            "name": "Jane Zhang",
                            "children": [{
                                "name": "Zhang Jie",
                                "children": [{
                                    "name": "happy family"
                                }, {
                                    "name": "Li"
                                }, {
                                    "name": "Hsiao"
                                }]
                            }, {
                                "name": "Reno",
                                "children": [{
                                    "name": "everyday brothers"
                                }, {
                                    "name": "martial arts",
                                    "children": [{
                                        "name": "Genie",
                                        "children": [{
                                            "name": "Liao Junjie",
                                            "children": [{
                                                "name": "Even Chen Xiang",
                                                "children": [{
                                                    "name": "Ma Zhenhuan"
                                                }, {
                                                    "name": "Xiao Huang Qi"
                                                }, {
                                                    "name": "Poetry An"
                                                }]
                                            }]
                                        }]
                                    }, {
                                        "name": "Ah Wei"
                                    }, {
                                        "name": "Ken Hung"
                                    }]
                                }, {
                                    "name": "DJ small strong"
                                }]
                            }, {
                                "name": "Timbaland"
                            }]
                        }, {
                            "name": "Purple Tang chess",
                            "children": [{
                                "name": "Li Yun",
                                "children": [{
                                    "name": "Chaolian",
                                    "children": [{
                                        "name": "Ken Wu",
                                        "children": [{
                                            "name": "Lawrence Ho Kai"
                                        }, {
                                            "name": "Chen Zeshan"
                                        }]
                                    }, {
                                        "name": "Ka Tina"
                                    }, {
                                        "name": "jw_amusic"
                                    }]
                                }, {
                                    "name": "Poetry Ya"
                                }, {
                                    "name": "Jing"
                                }]
                            }, {
                                "name": "Charlene",
                                "children": [{
                                    "name": "Gillian",
                                    "children": [{
                                        "name": "Qiao Zhenyu",
                                        "children": [{
                                            "name": "Ma Tianyu"
                                        }, {
                                            "name": "William Chan",
                                            "children": [{
                                                "name": "Mani"
                                            }]
                                        }, {
                                            "name": "High Weiguang"
                                        }]
                                    }, {
                                        "name": "Zhou Tang Hao"
                                    }, {
                                        "name": "Alien Huang"
                                    }]
                                }, {
                                    "name": "Xie Na"
                                }, {
                                    "name": "Chim"
                                }]
                            }, {
                                "name": "Shira",
                                "children": [{
                                    "name": "EXO-M"
                                }, {
                                    "name": "Moo."
                                }]
                            }]
                        }, {
                            "name": "Michelle Wai",
                            "children": [{
                                "name": "Sandra"
                            }, {
                                "name": "Matt Chow"
                            }, {
                                "name": "Sherman Chung"
                            }]
                        }, {
                            "name": "Joey",
                            "children": [{
                                "name": "Tony"
                            }, {
                                "name": "Wyman"
                            }]
                        }, {
                            "name": "Calvin",
                            "children": [{
                                "name": "Eason",
                                "children": [{
                                    "name": "Nicholas Tse",
                                    "children": [{
                                        "name": "Gui Lun Mei",
                                        "children": [{
                                            "name": "Shu Qi"
                                        }, {
                                            "name": "Mei"
                                        }, {
                                            "name": "Stefanie"
                                        }]
                                    }, {
                                        "name": "Lindane"
                                    }, {
                                        "name": "Yundi Li"
                                    }]
                                }, {
                                    "name": "Mavis"
                                }, {
                                    "name": "Zhang Bai",
                                    "children": [{
                                        "name": "Eddie",
                                        "children": [{
                                            "name": "Dante Lam"
                                        }]
                                    }, {
                                        "name": "Wei Chen",
                                        "children": [{
                                            "name": "Qin Kai",
                                            "children": [{
                                                "name": "Sun Yang",
                                                "children": [{
                                                    "name": "Jacky Cheung"
                                                }, {
                                                    "name": "Chinese Shao"
                                                }]
                                            }, {
                                                "name": "Wu Min Xia"
                                            }, {
                                                "name": "Chen Yibing"
                                            }]
                                        }]
                                    }, {
                                        "name": "Zhang Jiajia"
                                    }]
                                }]
                            }, {
                                "name": "Eric Kot"
                            }, {
                                "name": "Ekin Cheng",
                                "children": [{
                                    "name": "Jordan Chan"
                                }, {
                                    "name": "Michael Tse"
                                }]
                            }]
                        }, {
                            "name": "JJ",
                            "children": [{
                                "name": "Lee Hom"
                            }, {
                                "name": "Jolin"
                            }]
                        }, {
                            "name": "Xu Zheng",
                            "children": [{
                                "name": "Jet Li"
                            }, {
                                "name": "Han"
                            }, {
                                "name": "Zhao Wei"
                            }]
                        }, {
                            "name": "Liu follow Zimo",
                            "children": [{
                                "name": "Qian Xue"
                            }, {
                                "name": "Yang Shanshan"
                            }]
                        }, {
                            "name": "Wang Zijian",
                            "children": [{
                                "name": "Zheng Kai"
                            }, {
                                "name": "Liu Jiang"
                            }, {
                                "name": "Tao"
                            }]
                        }, {
                            "name": "Luo Zhen ring",
                            "children": [{
                                "name": "Zou Kai",
                                "children": [{
                                    "name": "Xu Song"
                                }, {
                                    "name": "Zhang Chenglong"
                                }, {
                                    "name": "Zou Shiming"
                                }]
                            }, {
                                "name": "Ju snow",
                                "children": [{
                                    "name": "Yu Jingxi",
                                    "children": [{
                                        "name": "Zhangxiang Ling",
                                        "children": [{
                                            "name": "PLU millet"
                                        }, {
                                            "name": "Highland level"
                                        }]
                                    }, {
                                        "name": "Dai Shi",
                                        "children": [{
                                            "name": "Yang Fengzhi"
                                        }, {
                                            "name": "Li Xin"
                                        }, {
                                            "name": "The Lu Wei",
                                            "children": [{
                                                "name": "Sun Yalong"
                                            }, {
                                                "name": "Shen Qu map"
                                            }, {
                                                "name": "Zhu rights"
                                            }]
                                        }]
                                    }, {
                                        "name": "Pei Yue",
                                        "children": [{
                                            "name": "Shen Weirong",
                                            "children": [{
                                                "name": "Jin Yibo"
                                            }, {
                                                "name": "Bian Zhengwei"
                                            }]
                                        }, {
                                            "name": "LI"
                                        }]
                                    }]
                                }, {
                                    "name": "Kong Lianshun",
                                    "children": [{
                                        "name": "old wet",
                                        "children": [{
                                            "name": "Extreme Jade"
                                        }, {
                                            "name": "Ma"
                                        }, {
                                            "name": "Yan potato avi"
                                        }]
                                    }, {
                                        "name": "Little Love"
                                    }, {
                                        "name": "Ma Poetry",
                                        "children": [{
                                            "name": "Zhang Yu"
                                        }]
                                    }]
                                }, {
                                    "name": "Sun Bowen",
                                    "children": [{
                                        "name": "Chen Jian book"
                                    }, {
                                        "name": "Chen Qidong"
                                    }, {
                                        "name": "Teng Lin Ji"
                                    }]
                                }]
                            }, {
                                "name": "Chen Jianhong",
                                "children": [{
                                    "name": "Yi",
                                    "children": [{
                                        "name": "Aaron"
                                    }, {
                                        "name": "Sandrine Pinna"
                                    }, {
                                        "name": "Liu Xiping"
                                    }]
                                }, {
                                    "name": "Peter Ho",
                                    "children": [{
                                        "name": "Yu Yongfu",
                                        "children": [{
                                            "name": "Chao"
                                        }, {
                                            "name": "Yu Chengdong"
                                        }, {
                                            "name": "Jin Chi",
                                            "children": [{
                                                "name": "Gary"
                                            }, {
                                                "name": "Weixue Man"
                                            }, {
                                                "name": "Zeng Yiming"
                                            }]
                                        }]
                                    }, {
                                        "name": "Dylan Kuo"
                                    }, {
                                        "name": "Wallace"
                                    }]
                                }, {
                                    "name": "Jang Keun Suk"
                                }]
                            }]
                        }, {
                            "name": "Tu Song Yan",
                            "children": [{
                                "name": "Haiqing"
                            }, {
                                "name": "Zhang Yi Yun"
                            }, {
                                "name": "Wang Yuan may"
                            }]
                        }, {
                            "name": "Gordon Chan",
                            "children": [{
                                "name": "package Bell",
                                "children": [{
                                    "name": "Chen He"
                                }, {
                                    "name": "Yang Shan"
                                }]
                            }]
                        }, {
                            "name": "stay a few hands",
                            "children": [{
                                "name": "Xiahe"
                            }, {
                                "name": "Lu Qi",
                                "children": [{
                                    "name": "Bei Zhicheng"
                                }, {
                                    "name": "Sun Jie"
                                }]
                            }, {
                                "name": "Zhang Xin Yuan",
                                "children": [{
                                    "name": "Huang Xuan"
                                }, {
                                    "name": "Yuki Furukawa"
                                }, {
                                    "name": "Jiang Jinfu"
                                }]
                            }]
                        }, {
                            "name": "Yumiko",
                            "children": [{
                                "name": "Leo",
                                "children": [{
                                    "name": "Cui origin",
                                    "children": [{
                                        "name": "Pu Zhengzhu"
                                    }, {
                                        "name": "Kim Hee Chul"
                                    }]
                                }, {
                                    "name": "Dayo"
                                }]
                            }]
                        }, {
                            "name": "Ning Hao",
                            "children": [{
                                "name": "Xu Zheng"
                            }, {
                                "name": "Huang Bo"
                            }, {
                                "name": "Ray good news"
                            }]
                        }, {
                            "name": "Niu Ze",
                            "children": [{
                                "name": "Chen meaning",
                                "children": [{
                                    "name": "Zhang Jun Ning"
                                }, {
                                    "name": "Chen Bo Lin,",
                                    "children": [{
                                        "name": "Feng Shao Feng"
                                    }, {
                                        "name": "High Huayang",
                                        "children": [{
                                            "name": "Crowding"
                                        }, {
                                            "name": "Li Donglin"
                                        }, {
                                            "name": "Summer Green"
                                        }]
                                    }]
                                }, {
                                    "name": "Ji Jin Hee"
                                }]
                            }, {
                                "name": "Ethan"
                            }]
                        }, {
                            "name": "Zhou Xianyang",
                            "children": [{
                                "name": "Wang Luo Dan"
                            }, {
                                "name": "Well Bo Ran"
                            }, {
                                "name": "Zhang Jin"
                            }]
                        }, {
                            "name": "Dee",
                            "children": [{
                                "name": "Kevin Tsai"
                            }]
                        }, {
                            "name": "Liu Wei",
                            "children": [{
                                "name": "Yangqi Yu",
                                "children": [{
                                    "name": "Eric"
                                }, {
                                    "name": "Zhang Aija",
                                    "children": [{
                                        "name": "Serena Fang"
                                    }]
                                }]
                            }, {
                                "name": "Raymond"
                            }]
                        }, {
                            "name": "Zhou Tang Hao",
                            "children": [{
                                "name": "Owodog",
                                "children": [{
                                    "name": "Luo"
                                }, {
                                    "name": "Jane Kaile"
                                }]
                            }, {
                                "name": "Lin Wei Heng"
                            }, {
                                "name": "Wang Xuee",
                                "children": [{
                                    "name": "Hong Wei Ning"
                                }]
                            }]
                        }, {
                            "name": "Ying Yang",
                            "children": [{
                                "name": "Ni-ni"
                            }]
                        }, {
                            "name": "Dong Zijian",
                            "children": [{
                                "name": "Guo Jingfei",
                                "children": [{
                                    "name": "Anita"
                                }, {
                                    "name": "Qian Fang"
                                }]
                            }, {
                                "name": "Lu Yi"
                            }, {
                                "name": "Kwan"
                            }]
                        }]
                    }
                    ]
                }
            ]
        };

        return (options);
    }
}
